import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import useThemeMode from "../../hooks/useThemeMode";
import { getMarketingTheme } from "../../utils/marketingTheme";
import useI18n from "../../i18n/useI18n";

const FeaturesHero = () => {
  const { isDark } = useThemeMode();
  const { t } = useI18n();
  const colors = useMemo(() => getMarketingTheme(isDark), [isDark]);

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const heroCards = useMemo(
    () => [
      {
        label: tt("featuresHero.cards.free.label", "FREE"),
        text: tt(
          "featuresHero.cards.free.text",
          "Hooks, Titles, Captions, Hashtags, Descriptions and Scripts."
        ),
      },
      {
        label: tt("featuresHero.cards.creator.label", "CREATOR"),
        text: tt(
          "featuresHero.cards.creator.text",
          "Premium workflow tools for packaging, rewrites, retention, CTAs, carousels, repurposing, and more."
        ),
      },
      {
        label: tt("featuresHero.cards.pro.label", "PRO"),
        text: tt(
          "featuresHero.cards.pro.text",
          "Advanced strategic tools for trend conversion, brand voice training, and content differentiation."
        ),
      },
      {
        label: tt("featuresHero.cards.oneStudio.label", "One Studio"),
        text: tt(
          "featuresHero.cards.oneStudio.text",
          "Create across hooks, titles, captions, scripts, rewrites, and advanced workflow tools in one place."
        ),
      },
    ],
    [t]
  );

  const footerStats = useMemo(
    () => [
      tt("featuresHero.footerStats.tools", "22 Studio tools"),
      tt("featuresHero.footerStats.tieredAccess", "Tiered access"),
      tt("featuresHero.footerStats.cleanerWorkflow", "Cleaner workflow"),
    ],
    [t]
  );

  return (
    <section
      style={{
        padding: "84px 20px 40px",
        background: colors.heroBg,
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "32px",
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 14px",
              borderRadius: "999px",
              background: colors.badgeBg,
              color: colors.badgeText,
              fontWeight: "700",
              fontSize: "12px",
              marginBottom: "18px",
              border: isDark ? `1px solid ${colors.border}` : "none",
            }}
          >
            {tt("featuresHero.badge", "Plan-based Studio access")}
          </div>

          <h1
            style={{
              fontSize: "clamp(2.3rem, 5vw, 4rem)",
              lineHeight: "1.04",
              margin: "0 0 16px",
              color: colors.text,
              letterSpacing: "-0.03em",
            }}
          >
            {tt(
              "featuresHero.title",
              "22 Studio tools built for modern creator workflow"
            )}
          </h1>

          <p
            style={{
              fontSize: "17px",
              lineHeight: "1.75",
              color: colors.textSoft,
              margin: "0 0 24px",
              maxWidth: "720px",
            }}
          >
            {tt(
              "featuresHero.subtitle",
              "Hookora now gives creators a clearer upgrade path with core writing tools on Free, premium workflow tools on Creator, and advanced strategic tools on Pro."
            )}
          </p>

          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <Link
              to="/register"
              style={{
                textDecoration: "none",
                background: "linear-gradient(135deg, #2563eb, #7c3aed)",
                color: "#fff",
                padding: "14px 18px",
                borderRadius: "12px",
                fontWeight: "800",
                boxShadow: "0 12px 28px rgba(79, 70, 229, 0.24)",
              }}
            >
              {tt("pricing.startFree", "Start Free")}
            </Link>

            <Link
              to="/pricing"
              style={{
                textDecoration: "none",
                background: colors.panelBg,
                color: colors.text,
                padding: "14px 18px",
                borderRadius: "12px",
                fontWeight: "800",
                border: `1px solid ${colors.borderStrong}`,
              }}
            >
              {tt("featuresHero.viewPricing", "View Pricing")}
            </Link>
          </div>
        </div>

        <div>
          <div
            style={{
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderRadius: "26px",
              padding: "22px",
              boxShadow: colors.shadow,
              backdropFilter: isDark ? "blur(14px)" : "none",
            }}
          >
            <div
              style={{
                display: "grid",
                gap: "12px",
              }}
            >
              {heroCards.map((item) => (
                <div
                  key={item.label}
                  style={{
                    background: colors.cardBgSoft,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "18px",
                    padding: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      padding: "5px 9px",
                      borderRadius: "999px",
                      background: colors.panelBg,
                      border: `1px solid ${colors.border}`,
                      color: colors.text,
                      fontSize: "12px",
                      fontWeight: "800",
                      marginBottom: "8px",
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    style={{
                      color: colors.textSoft,
                      lineHeight: "1.65",
                      fontSize: "14px",
                    }}
                  >
                    {item.text}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                marginTop: "14px",
                paddingTop: "14px",
                borderTop: `1px solid ${colors.border}`,
                color: colors.textMuted,
                fontSize: "13px",
                display: "flex",
                justifyContent: "space-between",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {footerStats.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesHero;